import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { WorkflowStatut, WorkflowStatutTransition } from '../models/models';
import api from './api';

interface WorkflowStatutState {
  statutsData: WorkflowStatut[];
  transitionsData: WorkflowStatutTransition[];
  transitionsDisponibles: WorkflowStatutTransition[];
  total: number;
  totalPages: number;
  currentPage: number;
  totalTransitions: number;
  totalPagesTransitions: number;
  currentPageTransitions: number;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
  selectedStatut: WorkflowStatut | null;
  selectedTransition: WorkflowStatutTransition | null;
}

const initialState: WorkflowStatutState = {
  statutsData: [],
  transitionsData: [],
  transitionsDisponibles: [],
  total: 0,
  totalPages: 1,
  currentPage: 1,
  totalTransitions: 0,
  totalPagesTransitions: 1,
  currentPageTransitions: 1,
  loading: false,
  error: null,
  successMessage: null,
  selectedStatut: null,
  selectedTransition: null,
};

// ── Statuts ────────────────────────────────────────────────────────────────

export const fetchWorkflowStatuts = createAsyncThunk(
  'workflowStatut/fetchStatuts',
  async (params: {
    departement_id?: number | null;
    doc_type_code?: string | null;
    limit: number;
    offset: number;
  }, { rejectWithValue }) => {
    try {
      const response = await api.get('/workflowStatut/statuts', { params });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur chargement statuts');
    }
  }
);

export const saveWorkflowStatut = createAsyncThunk(
  'workflowStatut/saveStatut',
  async (statut: WorkflowStatut, { rejectWithValue }) => {
    try {
      const response = await api.post('/workflowStatut/statuts', statut);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur enregistrement statut');
    }
  }
);

export const deleteWorkflowStatut = createAsyncThunk(
  'workflowStatut/deleteStatut',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete('/workflowStatut/statuts', { params: { id } });
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur suppression statut');
    }
  }
);

// ── Transitions ────────────────────────────────────────────────────────────

export const fetchWorkflowStatutTransitions = createAsyncThunk(
  'workflowStatut/fetchTransitions',
  async (params: {
    departement_id?: number | null;
    doc_type_code?: string | null;
    limit: number;
    offset: number;
  }, { rejectWithValue }) => {
    try {
      const response = await api.get('/workflowStatut/transitions', { params });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur chargement transitions');
    }
  }
);

export const saveWorkflowStatutTransition = createAsyncThunk(
  'workflowStatut/saveTransition',
  async (transition: WorkflowStatutTransition, { rejectWithValue }) => {
    try {
      const response = await api.post('/workflowStatut/transitions', transition);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur enregistrement transition');
    }
  }
);

export const deleteWorkflowStatutTransition = createAsyncThunk(
  'workflowStatut/deleteTransition',
  async (id: number, { rejectWithValue }) => {
    try {
      await api.delete('/workflowStatut/transitions', { params: { id } });
      return id;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur suppression transition');
    }
  }
);

// ── Transitions disponibles (par document) ─────────────────────────────────

export const fetchTransitionsDisponibles = createAsyncThunk(
  'workflowStatut/fetchTransitionsDisponibles',
  async (params: { doc_id: number; departement_id: number }, { rejectWithValue }) => {
    try {
      const response = await api.get(`/workflowStatut/document/${params.doc_id}/transitions`, {
        params: { departement_id: params.departement_id },
      });
      return response.data as WorkflowStatutTransition[];
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.message ?? 'Erreur chargement transitions disponibles');
    }
  }
);

// ── Slice ──────────────────────────────────────────────────────────────────

const WorkflowStatutSlice = createSlice({
  name: 'WorkflowStatutSlice',
  initialState,
  reducers: {
    setSelectedStatut(state, action: PayloadAction<WorkflowStatut | null>) {
      state.selectedStatut = action.payload;
    },
    setSelectedTransition(state, action: PayloadAction<WorkflowStatutTransition | null>) {
      state.selectedTransition = action.payload;
    },
    clearWorkflowStatutData(state) {
      state.statutsData = [];
      state.transitionsData = [];
      state.transitionsDisponibles = [];
      state.total = 0;
      state.totalPages = 1;
      state.currentPage = 1;
      state.totalTransitions = 0;
      state.totalPagesTransitions = 1;
      state.currentPageTransitions = 1;
    },
    clearMessages(state) {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    // fetchWorkflowStatuts
    builder
      .addCase(fetchWorkflowStatuts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowStatuts.fulfilled, (state, action) => {
        state.loading = false;
        state.statutsData  = action.payload.data;
        state.total        = action.payload.total;
        state.totalPages   = action.payload.totalPages;
        state.currentPage  = action.payload.currentPage;
      })
      .addCase(fetchWorkflowStatuts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // saveWorkflowStatut
    builder
      .addCase(saveWorkflowStatut.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveWorkflowStatut.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Statut enregistré avec succès';
        state.selectedStatut = null;
      })
      .addCase(saveWorkflowStatut.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // deleteWorkflowStatut
    builder
      .addCase(deleteWorkflowStatut.fulfilled, (state, action) => {
        state.successMessage = 'Statut supprimé';
        state.statutsData = state.statutsData.filter(s => s.id !== action.payload);
      })
      .addCase(deleteWorkflowStatut.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // fetchWorkflowStatutTransitions
    builder
      .addCase(fetchWorkflowStatutTransitions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkflowStatutTransitions.fulfilled, (state, action) => {
        state.loading = false;
        state.transitionsData         = action.payload.data;
        state.totalTransitions        = action.payload.total;
        state.totalPagesTransitions   = action.payload.totalPages;
        state.currentPageTransitions  = action.payload.currentPage;
      })
      .addCase(fetchWorkflowStatutTransitions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // saveWorkflowStatutTransition
    builder
      .addCase(saveWorkflowStatutTransition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveWorkflowStatutTransition.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Transition enregistrée avec succès';
        state.selectedTransition = null;
      })
      .addCase(saveWorkflowStatutTransition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // deleteWorkflowStatutTransition
    builder
      .addCase(deleteWorkflowStatutTransition.fulfilled, (state, action) => {
        state.successMessage = 'Transition supprimée';
        state.transitionsData = state.transitionsData.filter(t => t.id !== action.payload);
      })
      .addCase(deleteWorkflowStatutTransition.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // fetchTransitionsDisponibles
    builder
      .addCase(fetchTransitionsDisponibles.fulfilled, (state, action) => {
        state.transitionsDisponibles = action.payload;
      });
  },
});

export const {
  setSelectedStatut,
  setSelectedTransition,
  clearWorkflowStatutData,
  clearMessages,
} = WorkflowStatutSlice.actions;

export default WorkflowStatutSlice.reducer;
